/**
 * Verbes métier des commandes fournisseurs.
 *
 * `passerCommande` répartit un panier d'articles catalogue en une commande
 * par fournisseur — voir `create_material_orders_from_basket` côté base,
 * où vit la logique de répartition. N2 et non N3 : la commande créée ici
 * reste interne, rien n'est encore envoyé au fournisseur — ce jour-là
 * viendra un verbe distinct, à effet externe.
 */
import { z } from "zod";
import { ActionLevel } from "../../levels.js";
import { defineService } from "../../service.js";
import type { ExecutionContext } from "../../context.js";
import type { MaterialOrder, MaterialOrderStatus } from "./types.js";

export const createOrdersFromBasketInput = z.object({
  confirmationId: z.string().optional(),
  items: z
    .array(
      z.object({
        catalogItemId: z.string().uuid(),
        quantity: z.coerce.number().positive("La quantité doit être positive"),
      }),
    )
    .min(1, "Le panier est vide"),
  projectId: z.string().uuid().nullish(),
  neededBy: z.string().nullish(),
});

export type CreateOrdersFromBasketInput = z.infer<typeof createOrdersFromBasketInput>;

export const listMaterialOrdersInput = z.object({
  confirmationId: z.string().optional(),
  status: z
    .enum(["anticipated", "to_order", "ordered", "delivered", "partially_delivered", "cancelled"])
    .optional(),
});

export type ListMaterialOrdersInput = z.infer<typeof listMaterialOrdersInput>;

export const receiveMaterialOrderInput = z.object({
  confirmationId: z.string().optional(),
  orderId: z.string().uuid(),
});

export type ReceiveMaterialOrderInput = z.infer<typeof receiveMaterialOrderInput>;

export interface MaterialOrdersRepository {
  list(ctx: ExecutionContext, status?: MaterialOrderStatus): Promise<MaterialOrder[]>;
  createFromBasket(
    ctx: ExecutionContext,
    items: { catalogItemId: string; quantity: number }[],
    projectId: string | null,
    neededBy: string | null,
  ): Promise<MaterialOrder[]>;
  receive(ctx: ExecutionContext, orderId: string): Promise<void>;
}

export function createMaterialOrdersServices(repo: MaterialOrdersRepository) {
  const listerCommandes = defineService<ListMaterialOrdersInput, MaterialOrder[]>({
    name: "lister_commandes_materiaux",
    level: ActionLevel.Read,
    handler: (ctx, input) => repo.list(ctx, input.status),
  });

  /**
   * N2, réservé à l'encadrement — même restriction que le classement d'une
   * facture reçue : ça engage de l'argent, un ouvrier ne passe pas commande.
   */
  const passerCommande = defineService<CreateOrdersFromBasketInput, MaterialOrder[]>({
    name: "passer_commande_materiaux",
    level: ActionLevel.ReversibleWrite,
    allowedRoles: ["patron", "conducteur", "assistant"],
    handler: (ctx, input) =>
      repo.createFromBasket(
        ctx,
        input.items.map((i) => ({ catalogItemId: i.catalogItemId, quantity: i.quantity })),
        input.projectId ?? null,
        input.neededBy ?? null,
      ),
  });

  const receptionnerCommande = defineService<ReceiveMaterialOrderInput, void>({
    name: "receptionner_commande_materiaux",
    level: ActionLevel.ReversibleWrite,
    allowedRoles: ["patron", "conducteur", "assistant"],
    handler: (ctx, input) => repo.receive(ctx, input.orderId),
  });

  return { listerCommandes, passerCommande, receptionnerCommande };
}

export type MaterialOrdersServices = ReturnType<typeof createMaterialOrdersServices>;
